import React, { useCallback, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { formatDateWithClock } from '~/services/TimeService';

import { getLatestSyncedAt } from '~/utils/latestSyncedAt';

import TabPanelShell from '../../../shared/TabPanelShell';
import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import SortableTableHeaderCell from '../../shared/SortableTableHeaderCell';
import { useTableSort } from '../../shared/useTableSort';
import { UNMATCHED_ROW_ID, salesforceTableHeadSx, toggleExpandedRow } from '../surcharges/surchargeTabLayout';

import AppliedEgsTable from './AppliedEgsTable';
import EgsRoomRow from './EgsRoomRow';
import EgsSectionHeading from './EgsSectionHeading';
import EgsUnmatchedSection from './EgsUnmatchedSection';
import { buildAppliedEgsGroups } from './appliedEgsRows';
import {
  type EgsRoomMatch,
  buildEgsMatchByRoomLabel,
  countUnresolvedMatches,
  getEgsMatchStatusLabel,
  getEgsRoomRateCount,
  getRoomRateIds,
  getUnmatchedRoomRateIds,
} from './egsRoomMatcher';
import { countRoomRatesWithEgs, formatSlotList, groupEgsByRoom } from './egsUtils';
import type { EgsRoomGroup, EgsTabProps } from './types';

const EGS_TABLE_COLUMN_COUNT = 6;

type EgsSortField = 'room' | 'guestTypes' | 'rows' | 'status' | 'roomRates';

interface EgsSortRow {
  group: EgsRoomGroup;
  match: EgsRoomMatch;
  roomRateCount: number | null;
}

export default function EgsTab({
  canApplyEgs,
  error,
  extraGuestSurchargesByRoomRate,
  isLoading,
  isLoadingRoomRateEgs,
  isLoadingRoomTypes,
  onApplySalesforceEgs,
  propertyId,
  roomRateEgsError,
  roomTypes,
  roomTypesError,
  surcharges,
  vendorId,
}: EgsTabProps) {
  const [expandedRooms, setExpandedRooms] = useState<Set<string>>(new Set());
  const roomGroups = useMemo(() => groupEgsByRoom(surcharges), [surcharges]);
  const matchByRoomLabel = useMemo(
    () =>
      buildEgsMatchByRoomLabel(
        roomGroups.map((group) => group.roomLabel),
        roomTypes,
      ),
    [roomGroups, roomTypes],
  );
  const unmatchedRoomRateIds = useMemo(
    () =>
      getUnmatchedRoomRateIds({
        egsByRoomRateId: extraGuestSurchargesByRoomRate,
        matchByRoomLabel,
        roomTypes,
      }),
    [extraGuestSurchargesByRoomRate, matchByRoomLabel, roomTypes],
  );
  const unresolvedMatchCount = useMemo(() => countUnresolvedMatches(matchByRoomLabel), [matchByRoomLabel]);
  const egsSortRows = useMemo<Array<EgsSortRow>>(
    () =>
      roomGroups.map((group) => {
        const match = matchByRoomLabel[group.roomLabel];
        return {
          group,
          match,
          roomRateCount: getEgsRoomRateCount({
            error: roomTypesError,
            hasRoomTypes: roomTypes.length > 0,
            isLoading: isLoadingRoomTypes,
            match,
            roomTypes,
          }),
        };
      }),
    [roomGroups, matchByRoomLabel, roomTypesError, roomTypes, isLoadingRoomTypes],
  );
  const getEgsSortValue = useCallback(
    (row: EgsSortRow, field: EgsSortField) => {
      switch (field) {
        case 'room':
          return row.group.roomLabel;
        case 'guestTypes':
          return formatSlotList(row.group.slots);
        case 'rows':
          return row.group.rows.length;
        case 'status':
          return getEgsMatchStatusLabel({
            error: roomTypesError,
            hasRoomTypes: roomTypes.length > 0,
            isLoading: isLoadingRoomTypes,
            match: row.match,
          });
        case 'roomRates':
          return row.roomRateCount;
        default:
          return null;
      }
    },
    [roomTypesError, roomTypes, isLoadingRoomTypes],
  );
  const {
    sortField,
    sortDirection,
    handleSortChange,
    sortedRows: sortedEgsRows,
  } = useTableSort(egsSortRows, getEgsSortValue, 'room');
  const roomRatesWithEgs = useMemo(
    () => countRoomRatesWithEgs(extraGuestSurchargesByRoomRate),
    [extraGuestSurchargesByRoomRate],
  );
  const appliedGroups = useMemo(
    () =>
      buildAppliedEgsGroups({
        egsByRoomRateId: extraGuestSurchargesByRoomRate,
        propertyId,
        roomTypes,
        vendorId,
      }),
    [extraGuestSurchargesByRoomRate, propertyId, roomTypes, vendorId],
  );
  const totalRoomRates = useMemo(() => getRoomRateIds(roomTypes).length, [roomTypes]);
  const lastSyncedAt = useMemo(
    () => getLatestSyncedAt(surcharges.map((surcharge) => surcharge.synced_at)),
    [surcharges],
  );
  useDealInformationTabCount('egs', isLoading || error ? undefined : roomGroups.length);

  const hasRoomTypes = roomTypes.length > 0;
  const hasNoRoomRates = !isLoadingRoomTypes && !roomTypesError && !hasRoomTypes;

  const salesforceFacts = [
    `${roomGroups.length} room${roomGroups.length === 1 ? '' : 's'}`,
    lastSyncedAt ? `last synced ${formatDateWithClock(lastSyncedAt)}` : undefined,
  ].filter(Boolean);
  const roomRateFacts = [
    hasNoRoomRates ? 'none on this offer' : undefined,
    roomRateEgsError ? 'unavailable' : undefined,
    isLoadingRoomRateEgs || roomRateEgsError || hasNoRoomRates
      ? undefined
      : `${roomRatesWithEgs} of ${totalRoomRates} with surcharges`,
  ].filter(Boolean);
  const subtitle = [
    salesforceFacts.length ? `Salesforce: ${salesforceFacts.join(', ')}` : undefined,
    roomRateFacts.length ? `Room rates: ${roomRateFacts.join(', ')}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  /**
   * An unfinished or failed room rate load leaves the stored surcharges empty, which reads as nothing saved
   * and would make every write a create, duplicating records that already exist. So the apply waits for a
   * load that finished and worked, rather than opening on a plan that says create for every room.
   */
  const canApply = canApplyEgs && !isLoadingRoomRateEgs && !roomRateEgsError;

  // `canApplyEgs` also covers a missing property, which the tab cannot see, so anything but the room rate
  // cases falls back to the general statement rather than guessing.
  const applyDisabledReason = canApply
    ? undefined
    : roomRateEgsError
      ? 'This offer’s saved surcharges could not be loaded, so applying now could add a second surcharge to a room rate that already has one. Reload the page and try again.'
      : isLoadingRoomRateEgs
        ? 'Still loading the surcharges this offer already charges, so there is nothing to compare against yet.'
        : hasNoRoomRates
          ? 'This offer has no room rates, and a surcharge is saved against a room rate.'
          : 'This offer has nothing to apply Salesforce surcharges to yet.';

  const toggleRoom = useCallback(
    (roomLabel: string) => setExpandedRooms((current) => toggleExpandedRow(current, roomLabel)),
    [],
  );
  const toggleUnmatched = useCallback(() => toggleRoom(UNMATCHED_ROW_ID), [toggleRoom]);

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading extra guest surcharges.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load Salesforce extra guest surcharges.</Alert>;
  }

  return (
    <TabPanelShell
      title="Extra Guest Surcharges"
      subtitle={subtitle}
      contentSx={{ p: 0 }}
      actions={
        // A disabled button fires no pointer events, so the tooltip needs a wrapper to hang on.
        <Tooltip title={applyDisabledReason ?? ''}>
          <span>
            <Button disabled={!canApply} onClick={onApplySalesforceEgs} startIcon={<AddIcon />} variant="contained">
              Apply from Salesforce
            </Button>
          </span>
        </Tooltip>
      }
    >
      <AppliedEgsTable error={roomRateEgsError} groups={appliedGroups} isLoading={isLoadingRoomRateEgs} />

      <EgsSectionHeading
        description="Salesforce names each surcharge after the room it applies to, with one row per guest type. Expand a room to see its amounts and whether it matched a room rate on this offer."
        title="Salesforce EGS"
      />
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            {/* Blue for Salesforce data, grey on the applied table above for our own. */}
            <TableRow sx={salesforceTableHeadSx}>
              <TableCell />
              <SortableTableHeaderCell
                field="room"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Room
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="guestTypes"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Guest types
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="center"
                field="rows"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Rows
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="center"
                field="status"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Status
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="center"
                field="roomRates"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Room rates
              </SortableTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roomGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={EGS_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce extra guest surcharges found for this opportunity.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sortedEgsRows.map(({ group, match }) => (
              <EgsRoomRow
                key={group.roomLabel}
                columnCount={EGS_TABLE_COLUMN_COUNT}
                group={group}
                hasRoomTypes={hasRoomTypes}
                isExpanded={expandedRooms.has(group.roomLabel)}
                isLoadingRoomTypes={isLoadingRoomTypes}
                match={match}
                propertyId={propertyId}
                roomTypes={roomTypes}
                roomTypesError={roomTypesError}
                vendorId={vendorId}
                onToggleExpanded={toggleRoom}
              />
            ))}
            <EgsUnmatchedSection
              columnCount={EGS_TABLE_COLUMN_COUNT}
              error={roomRateEgsError}
              isExpanded={expandedRooms.has(UNMATCHED_ROW_ID)}
              isLoading={isLoadingRoomRateEgs}
              propertyId={propertyId}
              roomRateIds={unmatchedRoomRateIds}
              roomTypes={roomTypes}
              unresolvedMatchCount={unresolvedMatchCount}
              vendorId={vendorId}
              onToggleExpanded={toggleUnmatched}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanelShell>
  );
}
